import { t } from "i18next";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useAuth, useAutoSignin } from "react-oidc-context";

import KeycloakService from "#/data-layer/KeycloakService";
import env from "#/env";
import useConnectionState from "#/hooks/useConnectionState.js";
import ErrorPage from "#/pages/error/ErrorPage.jsx";

const keycloakService = new KeycloakService();

const Authenticated = ({ children, onToken, supportOfflineMode = false }) => {
  const auth = useAuth();
  const language = "sl"; // we have it hardcoded sine for now there is no language switcher
  useAutoSignin({
    signinArgs: {
      extraQueryParams: {
        ui_locales: language,
        module: t(env.APP_MODULE),
      },
    },
  });
  const [isTokenSet, setIsTokenSet] = useState(false);

  /**
   *  Detect a Keycloak-side logout (e.g. the user ends their session directly
   *  in the Keycloak account console) by periodically checking whether Keycloak
   *  still recognizes the access token. monitorSession/CheckSessionIFrame would
   *  do this near-instantly via a hidden iframe, but it hangs page load in this
   *  environment, so we poll instead.
   *
   *  TODO: check why monitorSession/CheckSessionIFrame hangs page load instead
   */
  useEffect(() => {
    if (!auth.isAuthenticated || !auth.user?.access_token) return undefined;

    const checkSession = async () => {
      const isValid = await keycloakService
        .isSessionValid(auth.user.access_token)
        .catch((error) => {
          console.error("Session check failed:", error);
          return true; // don't sign the user out on a transient/network error
        });
      if (!isValid) {
        await auth.removeUser();
      }
    };

    checkSession();
  }, [auth.isAuthenticated, auth.user?.access_token]);

  useEffect(() => {
    if (auth.user?.access_token) {
      setIsTokenSet(true);
      onToken(auth.user.access_token);
    }
  }, [auth.user?.access_token]);

  const { isOnline } = useConnectionState();

  // Bypass authentication if offline mode is supported and the user is offline.
  if (!isOnline && supportOfflineMode) return children;

  if (auth.error) {
    const isExpiredLogin =
      auth.error?.source === "renewSilent" &&
      auth.error?.innerError?.error === "invalid_grant";

    if (isExpiredLogin) {
      auth.removeUser().catch((error) => console.error(error)); // clear stale oidc user
      auth
        .signinRedirect({
          extraQueryParams: {
            ui_locales: language,
            module: t(env.APP_MODULE),
          },
        })
        .catch((error) => console.error(error));
      console.info("Token expired! Redirecting to login");
      return null;
    }

    if (auth.error.toString().includes("No matching state found in storage")) {
      // We do not really care about this error
      // just redirect back to root (and hope we do not get into a redirect loop)
      // eslint-disable-next-line no-restricted-globals, react-hooks/immutability
      location.href = "/";
      return null;
    }

    console.error("Authentication error:", auth.error);
    return <ErrorPage errorCode={500} errorMessage={auth.error.toString()} />;
  }

  // We also show loading if the token is not set yet.
  // Due to the asynchronous nature of the authentication process, there might be a delay before the token is available.
  if (auth.isLoading || !isTokenSet) {
    return <div className="main-content">Signing you in…</div>;
  }
  if (!auth.isAuthenticated) {
    return (
      <ErrorPage
        errorCode={401}
        errorMessage="You are not authenticated. Unable to login."
      />
    );
  }
  return children;
};

Authenticated.propTypes = {
  children: PropTypes.node,
  onToken: PropTypes.func,
};

export default Authenticated;
